import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut as fbSignOut } from 'firebase/auth';
import type { CurrentUser } from '@dmn/shared';
import { api, ApiError } from '@/lib/api';
import { AUTH_MODE, getFirebaseAuth, googleProvider } from '@/lib/firebase';

/**
 * Console authentication.
 *
 * Firebase Auth establishes *who* you are; the API decides whether you may be
 * here. `api.me()` is the authority on the admin role — the console never
 * inspects a custom claim itself, so revoking `dmn.admin` takes effect on the
 * next request rather than whenever a cached token happens to expire.
 *
 * `sponsor` is a third outcome alongside `signed-in` (admin) and `forbidden`
 * (neither): an account carrying a `sponsorId` but no `dmn.admin` claim. The
 * root route renders an entirely different shell for it — see `__root.tsx` —
 * never the admin one, so a sponsor session has no path to admin UI even if
 * every server-side check were somehow bypassed.
 */

interface AuthState {
  user: CurrentUser | null;
  status: 'loading' | 'signed-out' | 'signed-in' | 'sponsor' | 'forbidden';
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [error, setError] = useState<string | null>(null);

  const loadProfile = useCallback(async () => {
    try {
      const profile = await api.me();
      setUser(profile);
      setStatus(profile.isAdmin ? 'signed-in' : profile.sponsorId ? 'sponsor' : 'forbidden');
      setError(null);
    } catch (caught) {
      setUser(null);
      if (caught instanceof ApiError && caught.status === 403) {
        setStatus('forbidden');
        setError(caught.message);
        return;
      }
      setStatus('signed-out');
      setError(caught instanceof Error ? caught.message : 'Could not reach the DMN API');
    }
  }, []);

  useEffect(() => {
    // Emulator mode has no identity provider — the API trusts a header, and
    // `env.ts` refuses that arrangement outright when NODE_ENV=production.
    if (AUTH_MODE === 'emulator') {
      // `loadProfile` is async: every setState inside it happens after an
      // awaited fetch, so this is not the synchronous cascade the rule guards
      // against — it cannot see past the async boundary.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      void loadProfile();
      return;
    }

    return onAuthStateChanged(getFirebaseAuth(), (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setStatus('signed-out');
        return;
      }
      void loadProfile();
    });
  }, [loadProfile]);

  const signIn = useCallback(async () => {
    if (AUTH_MODE === 'emulator') {
      await loadProfile();
      return;
    }
    setError(null);
    try {
      await signInWithPopup(getFirebaseAuth(), googleProvider());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Sign-in failed');
    }
  }, [loadProfile]);

  const signOut = useCallback(async () => {
    if (AUTH_MODE !== 'emulator') await fbSignOut(getFirebaseAuth());
    setUser(null);
    setStatus('signed-out');
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, status, error, signIn, signOut }),
    [user, status, error, signIn, signOut]
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthState {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
